import { useState, useCallback, useEffect, useId } from "react";
import { useApi } from "@/hooks/use-api";
import { apiFetch } from "@/lib/api-client";
import { Skeleton, SkeletonCard, SkeletonTableRow } from "@/components/skeleton";

type AbStatus = "draft" | "running" | "paused" | "concluded";

interface AbVariant {
  id: string;
  name: string;
  versionId: string | null;
  isControl: boolean;
  impressions: number;
  conversions: number;
  conversionRate: number;
}

interface SignificanceResult {
  isSignificant: boolean;
  pValue: number;
  winnerVariantId: string | null;
  hasMinimumSample: boolean;
}

interface AbExperiment {
  id: string;
  pageId: string;
  name: string;
  status: AbStatus;
  trafficSplit: Record<string, number>;
  winnerId: string | null;
  concludedAt: string | null;
  createdBy: string;
  createdAt: string;
}

interface AbResults {
  experiment: AbExperiment;
  variants: AbVariant[];
  significance: SignificanceResult | null;
}

interface PageVersion {
  id: string;
  versionNumber: number;
  generationSource: string;
  createdAt: string;
}

interface PageVersionsResponse {
  items: PageVersion[];
  total: number;
}

type BadgeTone = "neutral" | "success" | "warning" | "primary";

const STATUS_STYLES: Record<AbStatus, string> = {
  draft: "bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]",
  running: "bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))]",
  paused: "bg-[hsl(var(--warning,40_90%_50%)/0.1)] text-[hsl(var(--warning,40_90%_50%))]",
  concluded: "bg-[hsl(var(--success,142_71%_45%)/0.1)] text-[hsl(var(--success,142_71%_45%))]",
};

const BADGE_TONE_STYLES: Record<BadgeTone, string> = {
  neutral: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
  success: "bg-[hsl(var(--success,142_71%_45%)/0.12)] text-[hsl(var(--success,142_71%_45%))]",
  warning: "bg-[hsl(var(--warning,40_90%_50%)/0.12)] text-[hsl(var(--warning,40_90%_50%))]",
  primary: "bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))]",
};

function StatusBadge({ status }: { status: AbStatus }) {
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[status]}`}>
      {status}
    </span>
  );
}

function InfoBadge({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: BadgeTone;
}) {
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${BADGE_TONE_STYLES[tone]}`}>
      {label}
    </span>
  );
}

function SummaryCard({
  label,
  value,
  subtext,
}: {
  label: string;
  value: string;
  subtext: string;
}) {
  return (
    <div className="rounded-md border border-[hsl(var(--border))] p-4">
      <p className="text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]">
        {label}
      </p>
      <p className="mt-2 text-2xl font-semibold text-[hsl(var(--foreground))]">{value}</p>
      <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{subtext}</p>
    </div>
  );
}

function ConversionRateBar({
  percent,
  maxPercent,
  isWinner,
}: {
  percent: number;
  maxPercent: number;
  isWinner: boolean;
}) {
  const width = maxPercent > 0 ? Math.max((percent / maxPercent) * 100, percent > 0 ? 8 : 0) : 0;

  return (
    <div
      aria-label="Conversion rate visual"
      className="h-2.5 w-28 overflow-hidden rounded-full bg-[hsl(var(--muted))]"
    >
      <div
        className={`h-full rounded-full ${isWinner ? "bg-[hsl(var(--success,142_71%_45%))]" : "bg-[hsl(var(--primary))]"}`}
        style={{ width: `${Math.min(width, 100)}%` }}
      />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Skeleton className="h-4 w-12" />
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
      <SkeletonCard className="border-[hsl(var(--border))]" />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <SkeletonCard className="border-[hsl(var(--border))]" />
        <SkeletonCard className="border-[hsl(var(--border))]" />
      </div>
      <div className="overflow-hidden rounded-md border border-[hsl(var(--border))]">
        {Array.from({ length: 3 }).map((_, index) => (
          <SkeletonTableRow key={index} cols={6} />
        ))}
      </div>
    </div>
  );
}

function buildEvenSplit(variantIds: string[]): Record<string, number> {
  if (variantIds.length === 0) {
    return {};
  }

  const base = Math.floor(100 / variantIds.length);
  let remainder = 100 - base * variantIds.length;

  return Object.fromEntries(
    variantIds.map((variantId) => {
      const nextValue = base + (remainder > 0 ? 1 : 0);
      if (remainder > 0) {
        remainder -= 1;
      }
      return [variantId, nextValue];
    }),
  );
}

function formatVersionLabel(version: PageVersion) {
  return `v${version.versionNumber} · ${version.generationSource} · ${new Date(version.createdAt).toLocaleDateString()}`;
}

export function AbExperimentDetail({
  pageId,
  experimentId,
  onBack,
}: {
  pageId: string;
  experimentId: string;
  onBack: () => void;
}) {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useApi<AbResults>(`/crm/pages/${pageId}/experiments/${experimentId}`);
  const {
    data: versionsData,
    isLoading: versionsLoading,
    refetch: refetchVersions,
  } = useApi<PageVersionsResponse>(`/crm/pages/${pageId}/versions?limit=50`);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [variantName, setVariantName] = useState("");
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [draftSplit, setDraftSplit] = useState<Record<string, number>>({});
  const variantNameId = useId();
  const variantVersionId = useId();

  useEffect(() => {
    if (!data) {
      return;
    }
    setDraftSplit(data.experiment.trafficSplit);
  }, [data]);

  useEffect(() => {
    if (data?.experiment.status !== "running" && data?.experiment.status !== "paused") {
      return;
    }

    const timer = setInterval(() => {
      refetch();
    }, 30_000);

    return () => clearInterval(timer);
  }, [data?.experiment.status, refetch]);

  const clearFeedback = useCallback(() => {
    setActionError(null);
    setActionMessage(null);
  }, []);

  const handleLifecycleAction = useCallback(async (action: string, body?: Record<string, unknown>) => {
    setActionLoading(true);
    clearFeedback();

    try {
      await apiFetch(`/crm/pages/${pageId}/experiments/${experimentId}/${action}`, {
        method: "POST",
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      setActionMessage(
        action === "start"
          ? "Experiment started."
          : action === "pause"
            ? "Experiment paused."
            : action === "resume"
              ? "Experiment resumed."
              : "Winner applied to the page.",
      );
      refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setActionLoading(false);
    }
  }, [clearFeedback, experimentId, pageId, refetch]);

  const handleAddVariant = useCallback(async () => {
    if (!selectedVersionId || !variantName.trim() || !data) {
      setActionError("Choose a saved page version and enter a variant name.");
      return;
    }

    setActionLoading(true);
    clearFeedback();

    try {
      const response = await apiFetch<{ data: AbVariant }>(`/crm/pages/${pageId}/experiments/${experimentId}/variants`, {
        method: "POST",
        body: JSON.stringify({
          name: variantName.trim(),
          versionId: selectedVersionId,
          isControl: data.variants.length === 0,
        }),
      });

      const updatedVariantIds = [...data.variants.map((variant) => variant.id), response.data.id];
      await apiFetch(`/crm/pages/${pageId}/experiments/${experimentId}/split`, {
        method: "PUT",
        body: JSON.stringify({ split: buildEvenSplit(updatedVariantIds) }),
      });

      setVariantName("");
      setSelectedVersionId("");
      setActionMessage("Variant added and traffic split rebalanced.");
      refetch();
      refetchVersions();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to add variant");
    } finally {
      setActionLoading(false);
    }
  }, [clearFeedback, data, experimentId, pageId, refetch, refetchVersions, selectedVersionId, variantName]);

  const handleSplitChange = useCallback((variantId: string, nextValue: string) => {
    const parsed = Number.parseInt(nextValue, 10);
    setDraftSplit((current) => ({
      ...current,
      [variantId]: Number.isFinite(parsed) ? parsed : 0,
    }));
  }, []);

  const handleBalanceSplit = useCallback(() => {
    if (!data || data.variants.length === 0) {
      return;
    }
    setDraftSplit(buildEvenSplit(data.variants.map((variant) => variant.id)));
  }, [data]);

  const handleSaveSplit = useCallback(async () => {
    if (!data) {
      return;
    }

    setActionLoading(true);
    clearFeedback();

    try {
      await apiFetch(`/crm/pages/${pageId}/experiments/${experimentId}/split`, {
        method: "PUT",
        body: JSON.stringify({ split: draftSplit }),
      });
      setActionMessage("Traffic split updated.");
      refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to update split");
    } finally {
      setActionLoading(false);
    }
  }, [clearFeedback, data, draftSplit, experimentId, pageId, refetch]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        <button onClick={onBack} className="text-sm text-[hsl(var(--primary))] hover:underline">
          Back to experiments
        </button>
        <p className="text-sm text-[hsl(var(--destructive))]">{error ?? "Experiment not found"}</p>
      </div>
    );
  }

  const { experiment, variants, significance } = data;
  const versions = versionsData?.items ?? [];
  const assignedVersionIds = new Set(
    variants
      .map((variant) => variant.versionId)
      .filter((versionId): versionId is string => Boolean(versionId)),
  );
  const availableVersions = versions.filter((version) => !assignedVersionIds.has(version.id));
  const confidencePct = significance ? Math.round((1 - significance.pValue) * 100) : null;
  const controlCount = variants.filter((variant) => variant.isControl).length;
  const splitSum = Object.values(draftSplit).reduce((sum, value) => sum + value, 0);
  const hasConfiguredSplit =
    variants.length > 0
    && variants.every((variant) => {
      const variantSplit = draftSplit[variant.id];
      return typeof variantSplit === "number" && variantSplit > 0;
    })
    && splitSum === 100;
  const startBlockedReason =
    variants.length < 2
      ? "Add at least two saved page versions before starting."
      : controlCount !== 1
        ? "Exactly one control variant is required."
        : !hasConfiguredSplit
          ? "Traffic split must assign every variant and total 100%."
          : null;
  const winnerVariantId = significance?.winnerVariantId ?? experiment.winnerId;
  const totalImpressions = variants.reduce((sum, variant) => sum + variant.impressions, 0);
  const totalConversions = variants.reduce((sum, variant) => sum + variant.conversions, 0);
  const overallConversionRate = totalImpressions > 0 ? (totalConversions / totalImpressions) * 100 : 0;
  const maxConversionRate = variants.reduce(
    (highest, variant) => Math.max(highest, variant.conversionRate * 100),
    0,
  );
  const significanceLabel = significance
    ? significance.isSignificant
      ? `Statistically significant${winnerVariantId ? " winner" : ""}`
      : significance.hasMinimumSample
        ? "No significant winner yet"
        : "Collecting sample"
    : experiment.status === "concluded" && experiment.winnerId
      ? "Winner applied"
      : experiment.status === "draft"
        ? "Draft configuration"
        : "Awaiting results";
  const significanceTone: BadgeTone = significance
    ? significance.isSignificant
      ? "success"
      : significance.hasMinimumSample
        ? "warning"
        : "primary"
    : experiment.status === "concluded" && experiment.winnerId
      ? "success"
      : "neutral";
  const sampleIndicatorLabel = significance
    ? significance.hasMinimumSample
      ? "Minimum sample reached"
      : "Below minimum sample"
    : experiment.status === "draft"
      ? "No live sample yet"
      : "Sample pending";
  const sampleIndicatorTone: BadgeTone = significance
    ? significance.hasMinimumSample
      ? "success"
      : "warning"
    : experiment.status === "draft"
      ? "neutral"
      : "primary";

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-sm text-[hsl(var(--primary))] hover:underline">
          Back
        </button>
        <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">{experiment.name}</h2>
        <StatusBadge status={experiment.status} />
      </div>

      <div className="rounded-md border border-[hsl(var(--border))] p-4">
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          This screen manages real experiment lifecycle and results. Variant content comes from existing saved page versions; it does not generate alternate variants itself.
        </p>
        {versionsLoading ? (
          <div className="mt-2 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-52" />
          </div>
        ) : versions.length === 0 ? (
          <p className="mt-2 text-sm text-[hsl(var(--destructive))]">
            This page has no saved versions yet. Create versions in the editor before configuring an experiment.
          </p>
        ) : (
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
            {versions.length} saved version{versions.length === 1 ? "" : "s"} available for this page.
          </p>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <SummaryCard
          label="Visitors"
          value={totalImpressions.toLocaleString()}
          subtext="Combined experiment impressions across all variants."
        />
        <SummaryCard
          label="Conversions"
          value={totalConversions.toLocaleString()}
          subtext="Goal conversions materialized on the experiment variants."
        />
        <SummaryCard
          label="Overall Rate"
          value={`${overallConversionRate.toFixed(1)}%`}
          subtext="Aggregate conversion rate across the current experiment."
        />
        <div className="rounded-md border border-[hsl(var(--border))] p-4">
          <p className="text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]">
            Result State
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <InfoBadge label={significanceLabel} tone={significanceTone} />
            <InfoBadge label={sampleIndicatorLabel} tone={sampleIndicatorTone} />
          </div>
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
            {significance
              ? significance.hasMinimumSample
                ? significance.isSignificant
                  ? `p = ${significance.pValue.toFixed(4)} • confidence ${confidencePct}%`
                  : `p = ${significance.pValue.toFixed(4)} • enough traffic, no winner yet`
                : `p = ${significance.pValue.toFixed(4)} • more traffic required before trusting a winner`
              : experiment.status === "draft"
                ? "Launch the experiment to start collecting evidence."
                : "Metrics refresh automatically while the experiment is live."}
          </p>
        </div>
      </div>

      {(actionError || actionMessage) && (
        <div className="rounded-md border border-[hsl(var(--border))] p-4">
          {actionError && <p className="text-sm text-[hsl(var(--destructive))]">{actionError}</p>}
          {actionMessage && <p className="text-sm text-[hsl(var(--foreground))]">{actionMessage}</p>}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {experiment.status === "draft" && (
          <button
            onClick={() => handleLifecycleAction("start")}
            disabled={actionLoading || startBlockedReason !== null}
            className="rounded-md bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary)/0.9)] disabled:opacity-50"
          >
            Start experiment
          </button>
        )}
        {experiment.status === "running" && (
          <button
            onClick={() => handleLifecycleAction("pause")}
            disabled={actionLoading}
            className="rounded-md border border-[hsl(var(--border))] px-4 py-2 text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] disabled:opacity-50"
          >
            Pause
          </button>
        )}
        {experiment.status === "paused" && (
          <button
            onClick={() => handleLifecycleAction("resume")}
            disabled={actionLoading}
            className="rounded-md bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary)/0.9)] disabled:opacity-50"
          >
            Resume
          </button>
        )}
        {(experiment.status === "running" || experiment.status === "paused") && winnerVariantId && significance?.isSignificant && (
          <button
            onClick={() => handleLifecycleAction("conclude", { winnerVariantId })}
            disabled={actionLoading}
            className="rounded-md bg-[hsl(var(--success,142_71%_45%))] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            Apply winner
          </button>
        )}
      </div>

      {experiment.status === "draft" && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <div className="space-y-4 rounded-md border border-[hsl(var(--border))] p-4">
            <div>
              <h3 className="font-semibold text-[hsl(var(--foreground))]">Add variant from saved version</h3>
              <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                Attach existing page versions as experiment variants. First variant becomes the control automatically.
              </p>
            </div>

            <div>
              <label htmlFor={variantNameId} className="block text-sm font-medium text-[hsl(var(--foreground))]">
                Variant name
              </label>
              <input
                id={variantNameId}
                type="text"
                value={variantName}
                onChange={(event) => setVariantName(event.target.value)}
                placeholder="e.g. CTA headline v2"
                className="mt-1 w-full rounded-md border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label htmlFor={variantVersionId} className="block text-sm font-medium text-[hsl(var(--foreground))]">
                Saved version
              </label>
              <select
                id={variantVersionId}
                value={selectedVersionId}
                onChange={(event) => setSelectedVersionId(event.target.value)}
                className="mt-1 w-full rounded-md border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-3 py-2 text-sm"
              >
                <option value="">Choose a version…</option>
                {availableVersions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {formatVersionLabel(version)}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleAddVariant}
              disabled={actionLoading || availableVersions.length === 0}
              className="rounded-md border border-[hsl(var(--border))] px-4 py-2 text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] disabled:opacity-50"
            >
              Add variant
            </button>

            {availableVersions.length === 0 && (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Every saved version for this page is already attached, or the page does not have alternative versions yet.
              </p>
            )}
          </div>

          <div className="space-y-4 rounded-md border border-[hsl(var(--border))] p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-[hsl(var(--foreground))]">Traffic split</h3>
                <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                  This uses the real backend split configuration. Variant removal is not exposed here yet, so only add versions you intend to test.
                </p>
              </div>
              <button
                onClick={handleBalanceSplit}
                disabled={variants.length === 0 || actionLoading}
                className="rounded-md border border-[hsl(var(--border))] px-3 py-2 text-xs font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] disabled:opacity-50"
              >
                Balance equally
              </button>
            </div>

            {variants.length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Add variants to configure traffic split.</p>
            ) : (
              <div className="space-y-3">
                {variants.map((variant) => (
                  <div key={variant.id} className="stagger-item flex items-center justify-between gap-4 rounded-md border border-[hsl(var(--border))] px-3 py-3">
                    <div>
                      <p className="font-medium text-[hsl(var(--foreground))]">{variant.name}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        {variant.isControl ? "Control" : "Treatment"} · {variant.versionId ? `Version ${variant.versionId.slice(0, 8)}` : "No version"}
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                      <span>Traffic %</span>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={draftSplit[variant.id] ?? 0}
                        onChange={(event) => handleSplitChange(variant.id, event.target.value)}
                        className="w-20 rounded-md border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-2 py-1 text-right text-sm text-[hsl(var(--foreground))]"
                      />
                    </label>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <span className="text-[hsl(var(--muted-foreground))]">Current total</span>
              <span className={splitSum === 100 ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--destructive))]"}>
                {splitSum}%
              </span>
            </div>

            <button
              onClick={handleSaveSplit}
              disabled={actionLoading || variants.length === 0}
              className="rounded-md bg-[hsl(var(--primary))] px-4 py-2 text-sm font-medium text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary)/0.9)] disabled:opacity-50"
            >
              Save split
            </button>

            {startBlockedReason && (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{startBlockedReason}</p>
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-[hsl(var(--border))]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]">
              <th className="px-4 py-3 text-left font-medium text-[hsl(var(--foreground))]">Variant</th>
              <th className="px-4 py-3 text-right font-medium text-[hsl(var(--foreground))]">Traffic</th>
              <th className="px-4 py-3 text-right font-medium text-[hsl(var(--foreground))]">Visitors</th>
              <th className="px-4 py-3 text-right font-medium text-[hsl(var(--foreground))]">Conversions</th>
              <th className="px-4 py-3 text-right font-medium text-[hsl(var(--foreground))]">Rate</th>
              <th className="px-4 py-3 text-right font-medium text-[hsl(var(--foreground))]">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {variants.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-[hsl(var(--muted-foreground))]">
                  No variants configured yet.
                </td>
              </tr>
            ) : (
              variants.map((variant) => {
                const isWinner = winnerVariantId === variant.id;
                const split = experiment.trafficSplit[variant.id] ?? draftSplit[variant.id] ?? 0;

                return (
                  <tr key={variant.id} className="stagger-item border-b border-[hsl(var(--border))] last:border-0">
                    <td className="px-4 py-3 text-[hsl(var(--foreground))]">
                      <span className="font-medium">{variant.name}</span>
                      {variant.isControl && (
                        <span className="ml-2 rounded bg-[hsl(var(--muted))] px-1.5 py-0.5 text-xs text-[hsl(var(--muted-foreground))]">
                          control
                        </span>
                      )}
                      {isWinner && (
                        <span className="ml-2 rounded bg-[hsl(var(--success,142_71%_45%)/0.1)] px-1.5 py-0.5 text-xs font-medium text-[hsl(var(--success,142_71%_45%))]">
                          winner
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[hsl(var(--foreground))]">{split}%</td>
                    <td className="px-4 py-3 text-right tabular-nums text-[hsl(var(--foreground))]">
                      {variant.impressions.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[hsl(var(--foreground))]">
                      {variant.conversions.toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-end gap-1.5">
                        <span className="tabular-nums text-[hsl(var(--foreground))]">
                          {(variant.conversionRate * 100).toFixed(1)}%
                        </span>
                        <ConversionRateBar
                          percent={variant.conversionRate * 100}
                          maxPercent={maxConversionRate}
                          isWinner={isWinner}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-[hsl(var(--muted-foreground))]">
                      {variant.isControl ? "--" : confidencePct !== null ? `${confidencePct}%` : "--"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="rounded-md border border-[hsl(var(--border))] p-4">
        {(experiment.status === "running" || experiment.status === "paused") && (
          <p className="mb-3 text-xs uppercase tracking-[0.14em] text-[hsl(var(--muted-foreground))]">
            Auto-refreshes every 30 seconds while the experiment is live.
          </p>
        )}
        {significance ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            {!significance.hasMinimumSample
              ? "The backend has not reached its minimum sample threshold yet, so significance is informative only."
              : significance.isSignificant
                ? `The current result is statistically significant (p = ${significance.pValue.toFixed(4)}, confidence ${confidencePct}%).`
                : `The backend has not declared a significant result yet (p = ${significance.pValue.toFixed(4)}).`}
          </p>
        ) : experiment.status === "concluded" && experiment.winnerId ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            This experiment is concluded. The winner has already been applied to the page.
          </p>
        ) : (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Results appear here once the experiment is running. Draft experiments only show configuration state.
          </p>
        )}
      </div>
    </div>
  );
}
